import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AdminSupportApiService } from '../API/admin-support-api.service';
import { MessageDialogService } from 'src/app/components/common/message-dialog/services/message-dialog.service';
import {
  AdminSupportCountsView,
  AdminSupportListItemView,
  StatFilterKind,
  SupportStatus,
  SupportThreadView,
} from 'src/app/common/models/support/admin-support.models';

const DEFAULT_PAGE_SIZE = 50;

export type AdminSupportDashboardState = {
  tab: SupportStatus;
  member: string | null;
  statFilter: StatFilterKind | null;
  cases: AdminSupportListItemView[];
  page: number;
  pageSize: number;
  totalCount: number;
  counts: AdminSupportCountsView | null;
  activeCaseId: string | null;
  thread: SupportThreadView | null;
  loading: boolean;
  sendingReply: boolean;
  closingCase: boolean;
};

const initialState: AdminSupportDashboardState = {
  tab: 'Open',
  member: null,
  statFilter: null,
  cases: [],
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalCount: 0,
  counts: null,
  activeCaseId: null,
  thread: null,
  loading: false,
  sendingReply: false,
  closingCase: false,
};

export const AdminSupportDashboardStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    api: inject(AdminSupportApiService),
    messageDialogService: inject(MessageDialogService),
    translate: inject(TranslateService),
  })),
  withMethods((store) => {
    // Shared failure path for every async action below — surfaces the same
    // modal-dialog pattern app.component.ts already uses for user-facing
    // errors elsewhere in this app (no toast/snackbar mechanism exists here).
    function reportError(err: unknown) {
      console.error('[AdminSupportDashboard]', err);
      store.messageDialogService.openError(
        store.translate.instant('admin_support.lbl_error_title'),
        store.translate.instant('admin_support.lbl_error_heading'),
        store.translate.instant('admin_support.lbl_error_body')
      );
    }

    // Guards against out-of-order responses: rapid tab switches, page changes, or
    // search input fire overlapping getCases requests, and a slower earlier request
    // can otherwise resolve after a faster later one and overwrite fresher state with
    // stale data. Each call captures the current generation; if another call has
    // started (and bumped the counter) by the time this one resolves, its result is
    // simply dropped instead of applied.
    let casesRequestId = 0;

    async function loadCasesInternal() {
      const requestId = ++casesRequestId;
      patchState(store, { loading: true });
      try {
        const channel = store.statFilter() === 'email' ? 'Email' : store.statFilter() === 'webapp' ? 'WebApp' : undefined;
        const waitingOnUs = store.statFilter() === 'waiting' ? true : undefined;
        const needsAdminReview = store.statFilter() === 'new' ? true : undefined;
        const result = await firstValueFrom(store.api.getCases({
          status: store.tab(),
          channel,
          waitingOnUs,
          needsAdminReview,
          member: store.member() ?? undefined,
          page: store.page(),
          pageSize: store.pageSize(),
        }));
        if (requestId !== casesRequestId) return;
        patchState(store, { cases: result.items, totalCount: result.totalCount, loading: false });
      } catch (err) {
        if (requestId !== casesRequestId) return;
        patchState(store, { loading: false });
        reportError(err);
      }
    }

    // Counts are global (not scoped to the current tab/filter) — any action that
    // can change a case's Open/Closed status or waiting-on-us flag (reply, close)
    // must refresh them too, or the stat cards go stale relative to the table.
    async function loadCountsInternal() {
      try {
        const counts = await firstValueFrom(store.api.getCounts());
        patchState(store, { counts });
      } catch (err) {
        reportError(err);
      }
    }

    // Plain (non-reactive) async methods: every action here is an imperative,
    // user-triggered call site (button click, tab switch, etc.) that the caller
    // awaits directly — not a value/Signal fed into a long-lived reactive watcher.
    // `signalMethod` is built for that latter case (see ChatStore.selectThread)
    // and its wrapper always requires an `input` argument and swallows the
    // processing function's returned Promise, which breaks both the niladic
    // methods here (loadCases/backToList/closeCase — TS2554: expected 1-2
    // arguments, got 0) and `await store.x()` callers relying on completion.
    // Plain async functions match this store's actual (imperative) usage and
    // are consistent with the non-reactive methods already on ChatStore
    // (setPendingRetry, addHistoryMessage, clearHistory, setThreadId).
    return {
      loadCases: async () => {
        await loadCasesInternal();
      },

      // Switching tabs starts that tab fresh: any in-progress search is
      // cleared, not carried over.
      selectTab: async (tab: SupportStatus) => {
        patchState(store, { tab, statFilter: null, member: null, page: 1 });
        await loadCasesInternal();
      },

      setMember: async (member: string | null) => {
        patchState(store, { member, page: 1 });
        await loadCasesInternal();
      },

      setPage: async (page: number) => {
        patchState(store, { page });
        await loadCasesInternal();
      },

      openThread: async (supportId: string) => {
        patchState(store, { activeCaseId: supportId, loading: true });
        try {
          const thread = await firstValueFrom(store.api.getThread(supportId));
          patchState(store, { thread, loading: false });
        } catch (err) {
          // Falls back to the case list rather than leaving activeCaseId set with
          // no thread — otherwise the panel renders an empty thread instead of
          // recovering to something the admin can act on.
          patchState(store, { activeCaseId: null, loading: false });
          reportError(err);
        }
      },

      backToList: async () => {
        patchState(store, { activeCaseId: null, thread: null });
        await loadCasesInternal();
      },

      // Called on every dashboard entry (component ngOnInit), not just the
      // first: the store is providedIn: 'root', so a prior visit's thread
      // (activeCaseId/thread) and stat filter can otherwise leak into a new
      // component instance as stale, hidden state. Reset the list-view state
      // and reload fresh, tab-scoped data.
      resetOnEntry: async () => {
        patchState(store, { activeCaseId: null, thread: null, statFilter: null, page: 1 });
        await loadCasesInternal();
      },

      sendReply: async (message: string) => {
        const id = store.activeCaseId();
        if (!id) return;
        patchState(store, { sendingReply: true });
        try {
          const result = await firstValueFrom(store.api.reply(id, message));
          // Captured before the refreshes below — the reply already happened and its
          // outcome is already known, so a later refresh failure must never suppress
          // this dialog or make it look like the reply itself was lost.
          const emailFailed = result?.emailDeliveryFailed;
          // All three are independent round-trips — no reason to serialize any of
          // them just because getThread's result also feeds patchState below. The
          // patchState happens in getThread's own .then, as soon as it resolves,
          // rather than waiting on the slower of the three refreshes.
          // A single .catch() on the combined promise (rather than a nested
          // try/catch) isolates a refresh failure from the emailFailed check below.
          // Uses a distinct message from reportError's generic one: the reply
          // itself already succeeded, so telling the admin the same thing shown
          // for a failed reply risks a confusing, duplicate resubmission.
          await Promise.all([
            firstValueFrom(store.api.getThread(id)).then((thread) => patchState(store, { thread })),
            loadCasesInternal(),
            loadCountsInternal(),
          ]).catch((refreshErr) => {
            console.error('[AdminSupportDashboard]', refreshErr);
            store.messageDialogService.openError(
              store.translate.instant('admin_support.lbl_reply_saved_refresh_failed_title'),
              store.translate.instant('admin_support.lbl_reply_saved_refresh_failed_heading'),
              store.translate.instant('admin_support.lbl_reply_saved_refresh_failed_body')
            );
          });
          // Shown once the thread (with its own persisted indicator) has had a chance
          // to become visible — but unconditionally on emailFailed, so a refresh
          // failure above can never silently swallow this signal.
          if (emailFailed) {
            store.messageDialogService.openInfoSingleBtn(
              store.translate.instant('admin_support.lbl_email_delivery_failed_title'),
              store.translate.instant('admin_support.lbl_email_delivery_failed_heading'),
              store.translate.instant('admin_support.lbl_email_delivery_failed_body')
            );
          }
        } catch (err) {
          reportError(err);
        } finally {
          patchState(store, { sendingReply: false });
        }
      },

      closeCase: async () => {
        const id = store.activeCaseId();
        if (!id || store.closingCase()) return;
        const confirmed = await firstValueFrom(store.messageDialogService.openCloseCaseConfirmation());
        if (!confirmed) return;
        patchState(store, { closingCase: true });
        try {
          await firstValueFrom(store.api.close(id));
          const thread = await firstValueFrom(store.api.getThread(id));
          patchState(store, { thread });
          await Promise.all([loadCasesInternal(), loadCountsInternal()]);
        } catch (err) {
          reportError(err);
        } finally {
          // Cleared regardless of outcome: if api.close() succeeded but the
          // follow-up getThread() failed, the case is closed server-side, so
          // leaving the button disabled would trap the admin with no way to
          // dismiss the state; re-enabling lets a genuine retry go through
          // (the backend simply rejects re-closing an already-closed case).
          patchState(store, { closingCase: false });
        }
      },

      loadCounts: async () => {
        await loadCountsInternal();
      },

      applyStatFilter: async (kind: StatFilterKind) => {
        const wasActive = store.statFilter() === kind;
        patchState(store, { statFilter: wasActive ? null : kind, tab: 'Open', page: 1 });
        await loadCasesInternal();
      },

      // Only called from the "Open cases" stat card, which should always jump to
      // the Open tab — matches applyStatFilter's own `tab: 'Open'` reset, so
      // clicking it while on the Closed tab doesn't silently reload closed cases.
      clearStatFilter: async () => {
        patchState(store, { statFilter: null, tab: 'Open', page: 1 });
        await loadCasesInternal();
      },
    };
  })
);
